import React, { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { AnimatePresence, motion } from "framer-motion"
import { z } from "zod"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form"
import { toast } from "sonner"
import { useAuth } from '@/providers/AuthProvider'
import { useRouter } from '@tanstack/react-router'
import { loginAPI } from '@/providers/authApi'
import { authStore } from '@/providers/authStore'
import { httpClient } from '@/httpClient'
import { ChevronRight, Store, Eye, EyeOff } from 'lucide-react'

const API_URL = import.meta.env.VITE_API_URL as string

const passwordSchema = z.object({
  password: z.string().min(8, "Password must be at least 8 characters"),
  passwordRepeat: z.string().min(8, "Password must be at least 8 characters")
})

type PasswordFormData = z.infer<typeof passwordSchema>


const Index = () => {
  const [step, setStep] = useState<'email' | 'password'>('password')
  const [email, setEmail] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const auth = useAuth()
  const router = useRouter()
  const [showPassword, setShowPassword] = useState(false)
  
  const signIn = async (data: PasswordFormData) => {
    try {
      const res = await httpClient().post(API_URL + '/public/me/change_password', {
        password: data.password,
        password_repeat: data.passwordRepeat,
      })
      if (res.status !== 200) {
        throw new Error('Invalid credentials')
      }
      const meRes = await httpClient().get(API_URL + '/public/me')
      const user = meRes.data.data
      authStore.setUser(user)
      setIsLoading(false)
      router.navigate({
        to: "/home"
      })
    } catch (error: any) {
      console.error("Reset Password error:", error)
      setIsLoading(false)
      const errorMessage = error?.response?.data?.message || error?.message || 'An error occurred. Please try again later.'
      toast.error(errorMessage)
      throw error
    }
  }

  // Password form
  const passwordForm = useForm<PasswordFormData>({
    resolver: zodResolver(passwordSchema),
    defaultValues: {
      password: '',
      passwordRepeat: ''
    }
  })

  const onPasswordSubmit = async (data: PasswordFormData) => {
    try {
      setIsLoading(true)
      await signIn(data)
      toast.success('Password changed successfully')
    } catch (error: any) {
      console.error("Password submit error:", error)
      // Set form error
      passwordForm.setError('password', {
        type: 'manual',
        message: error?.response?.data?.message || error?.message || 'Invalid credentials'
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="relative min-h-screen w-full flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0 bg-[#19213D]">
        <div 
          className="absolute w-[150%] h-[150%] -top-[25%] -left-[25%] opacity-80"
          style={{
            background: `conic-gradient(from 45deg at 50% 50%,#474DFF 0deg,transparent 60deg,#474DFF 180deg,transparent 240deg,#474DFF 360deg)`,
            transform: 'rotate(-12deg)',
            filter: 'blur(60px)'
          }}
        />
      </div>
      
      <div className="relative z-10 flex flex-col items-center gap-8">
        <AnimatePresence mode="wait">
            <motion.div
              key="password-logos"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="flex items-center gap-4"
            >
              <img src="/logo.svg" alt="Logo" className="w-12 h-auto" />
              <div className="flex items-center gap-[-8px]">
                <ChevronRight className="w-6 h-6 text-white" />
                <ChevronRight className="w-6 h-6 text-white -ml-4" />
                <ChevronRight className="w-6 h-6 text-white -ml-4" />
              </div>
              <div className="w-12 h-12 rounded-full bg-[#2C2C35] flex items-center justify-center">
                <Store className="w-6 h-6 text-white" />
              </div>
            </motion.div>
        </AnimatePresence>
        
        <AnimatePresence mode="wait">
          (
            <motion.div
              key="password"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="relative z-10"
            >
              <Card className="w-[400px] h-[320px] bg-[#1C1C24] border-0">
                <CardHeader>
                  <CardTitle className="text-2xl font-semibold text-white text-center">Change Password</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <Form {...passwordForm}>
                    <form onSubmit={passwordForm.handleSubmit(onPasswordSubmit)} className="space-y-6">
                      <div className="space-y-4">
                        <FormField
                          control={passwordForm.control}
                          name="password"
                          render={({ field }) => (
                            <FormItem>
                              <Label className="text-sm text-gray-300">Password</Label>
                              <FormControl>
                                <div className="relative">
                                  <Input 
                                    {...field}
                                    type={showPassword ? "text" : "password"}
                                    className="bg-[#2C2C35] border-gray-700 text-white focus:border-[#474DFF] focus:ring-[#474DFF] pr-10"
                                    placeholder="Enter your password"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                                  >
                                    {showPassword ? (
                                      <EyeOff className="w-4 h-4" />
                                    ) : (
                                      <Eye className="w-4 h-4" />
                                    )}
                                  </button>
                                </div>
                              </FormControl>
                              <FormMessage className="text-red-500" />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={passwordForm.control}
                          name="passwordRepeat"
                          render={({ field }) => (
                            <FormItem>
                              <Label className="text-sm text-gray-300">Repeat Password</Label>
                              <FormControl>
                                <div className="relative">
                                  <Input 
                                    {...field}
                                    type={showPassword ? "text" : "password"}
                                    className="bg-[#2C2C35] border-gray-700 text-white focus:border-[#474DFF] focus:ring-[#474DFF] pr-10"
                                    placeholder="Enter your password"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                                  >
                                    {showPassword ? (
                                      <EyeOff className="w-4 h-4" />
                                    ) : (
                                      <Eye className="w-4 h-4" />
                                    )}
                                  </button>
                                </div>
                              </FormControl>
                              <FormMessage className="text-red-500" />
                            </FormItem>
                          )}
                        />
                      </div>

                      <Button 
                        type="submit"
                        disabled={isLoading}
                        className="w-full bg-[#09090B] text-white hover:bg-[#09090B]/90"
                      >
                        {isLoading ? 'Loading...' : 'Change Password'}
                      </Button>
                    </form>
                  </Form>
                </CardContent>
              </Card>
            </motion.div>
          )
        </AnimatePresence>
      </div>
    </div>
  )
}

export default Index